import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../api";
import { useApp } from "../context/AppContext";
import { MODULES, ORDER_STATUS, fmtCHF, fmtDate, orderSummary } from "../utils";
import { AlertTriangle, Banknote, FileWarning, ReceiptText, Wallet } from "lucide-react";
import { PieChart, Pie, Cell } from "recharts";

const MOIS_FR = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];
const PTYPES = [["", "Tous"], ["conciergerie", "Conciergerie"], ["nettoyage", "Nettoyage"], ["laundry", "Blanchisserie"], ["refacturation", "Refacturation"]];
const REF_CARD = { key: "refacturation", label: "Refacturation", dot: "bg-violet-500", color: "#7c3aed" };

export default function Dashboard() {
  const { module } = useApp();
  const [data, setData] = useState(null);
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [monthNum, setMonthNum] = useState(now.getMonth() + 1);
  const [ptype, setPtype] = useState("");
  const selMonth = `${year}-${String(monthNum).padStart(2, "0")}`;
  const monthLabel = `${MOIS_FR[monthNum - 1]} ${year}`;
  const ptypeLabel = PTYPES.find(([k]) => k === ptype)[1];

  useEffect(() => {
    const params = { month: selMonth };
    if (ptype) params.ptype = ptype;
    api.get("/dashboard", { params }).then((r) => setData(r.data));
  }, [selMonth, ptype]);

  if (!data) return <div className="p-8 text-slate-500" data-testid="dashboard-loading">Chargement…</div>;

  const years = [];
  for (let y = now.getFullYear() + 1; y >= 2024; y--) years.push(y);

  const kpis = [
    { label: `CA ${monthLabel} (TTC)`, note: ptype ? `mois sélectionné · type : ${ptypeLabel}` : "mois sélectionné · selon date de facture", value: fmtCHF(data.ca_month), icon: Wallet, testid: "kpi-ca-month" },
    { label: "Factures impayées", note: "à ce jour, tous types", value: `${data.unpaid_count} · ${fmtCHF(data.unpaid_total)}`, icon: FileWarning, testid: "kpi-unpaid" },
    { label: "Bons non facturés", note: ptype ? `à ce jour · type : ${ptypeLabel}` : "à ce jour, tous types", value: `${data.uninvoiced_count} · ${fmtCHF(data.uninvoiced_total)}`, icon: AlertTriangle, testid: "kpi-uninvoiced" },
    { label: "Période affichée", note: "filtre du tableau de bord", value: monthLabel, icon: Banknote, testid: "kpi-month" },
  ];

  const SECTORS_META = [["conciergerie", "Conciergerie", "#0ea5e9"], ["nettoyage", "Nettoyage", "#10b981"], ["laundry", "Blanchisserie", "#fb7185"], ["refacturation", "Refacturation", "#8b5cf6"]];
  const sectorData = SECTORS_META.map(([key, name, color]) => ({ key, name, color, value: data.ca_sectors?.[key] || 0 })).filter((s) => s.value > 0);
  const sectorTotal = Math.round(sectorData.reduce((s, x) => s + x.value, 0) * 100) / 100;

  const allCards = [...Object.entries(MODULES).map(([k, M]) => ({ key: k, label: M.label, dot: M.dot, color: M.color })), REF_CARD];
  const cardsToShow = module === "tous" ? allCards : allCards.filter((c) => c.key === module);
  const recent = module === "tous" ? data.recent_orders : data.recent_orders.filter((o) => o.module === module);

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="dashboard-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-xl font-semibold text-slate-900">Tableau de bord</h1>
        <div className="flex items-center gap-2 flex-wrap" data-testid="dashboard-filters">
          <select data-testid="dashboard-month-select" value={monthNum} onChange={(e) => setMonthNum(Number(e.target.value))}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white">
            {MOIS_FR.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
          </select>
          <select data-testid="dashboard-year-select" value={year} onChange={(e) => setYear(Number(e.target.value))}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white">
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <div className="flex gap-1.5" data-testid="dashboard-ptype-filters">
            {PTYPES.map(([k, label]) => (
              <button key={k} data-testid={`dashboard-ptype-${k || "all"}`} onClick={() => setPtype(k)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${ptype === k ? "bg-slate-900 text-white border-transparent" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-100"}`}>
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {data.uninvoiced_count > 0 && (
        <Link to="/factures" data-testid="unbilled-orders-alert"
          className="flex items-center gap-3 bg-amber-50 border border-amber-300 text-amber-900 rounded-xl px-5 py-4 hover:bg-amber-100 transition-colors">
          <AlertTriangle className="text-amber-600 shrink-0" size={22} />
          <div>
            <div className="font-semibold text-sm">{data.uninvoiced_count} bon(s) réalisé(s) non facturé(s) — {fmtCHF(data.uninvoiced_total)} HT en attente de facturation</div>
            <div className="text-xs text-amber-700">Cumul à ce jour{ptype ? `, type : ${ptypeLabel}` : ""}. Cliquez pour lancer la facturation et éviter le manque à gagner.</div>
          </div>
        </Link>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((k) => (
          <div key={k.testid} data-testid={k.testid} className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{k.label}</span>
              <k.icon size={18} className="text-slate-400" />
            </div>
            <div className="mt-2 text-2xl font-semibold text-slate-900">{k.value}</div>
            <div className="mt-1 text-[11px] font-medium text-slate-400">{k.note}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {cardsToShow.map((c) => {
          const s = data.by_module[c.key] || { ca: 0, orders: 0 };
          return (
            <div key={c.key} data-testid={`module-card-${c.key}`} className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
              <div className="flex items-center gap-2">
                <span className={`w-2.5 h-2.5 rounded-full ${c.dot}`} />
                <span className="font-semibold text-slate-900">{c.label}</span>
              </div>
              <div className="mt-3 text-xl font-semibold" style={{ color: c.color }}>{fmtCHF(s.ca)} <span className="text-xs font-normal text-slate-400">HT · mois sélectionné</span></div>
              <div className="text-xs text-slate-500 mt-1">{s.orders} bon(s) facturé(s)</div>
            </div>
          );
        })}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm" data-testid="ca-sectors-block">
        <div className="text-sm font-semibold text-slate-900">Répartition du CA par secteur</div>
        <div className="text-[11px] text-slate-400 mt-0.5">HT après rabais · mois sélectionné ({monthLabel})</div>
        {sectorTotal > 0 ? (
          <div className="mt-4 flex items-center gap-6 flex-wrap">
            <PieChart width={200} height={200}>
              <Pie data={sectorData} dataKey="value" nameKey="name" innerRadius={55} outerRadius={85} strokeWidth={2} stroke="#fff" isAnimationActive={false}>
                {sectorData.map((s) => <Cell key={s.key} fill={s.color} />)}
              </Pie>
            </PieChart>
            <div className="space-y-2" data-testid="ca-sectors-legend">
              {sectorData.map((s) => (
                <div key={s.key} className="flex items-center gap-2 text-sm" data-testid={`ca-sector-${s.key}`}>
                  <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: s.color }} />
                  <span className="text-slate-600 w-28">{s.name}</span>
                  <span className="font-semibold text-slate-900">{fmtCHF(s.value)}</span>
                  <span className="text-xs text-slate-400">{Math.round((s.value / sectorTotal) * 100)} %</span>
                </div>
              ))}
              <div className="pt-1.5 border-t border-slate-100 text-sm font-semibold text-slate-900" data-testid="ca-sectors-total">Total {fmtCHF(sectorTotal)} HT</div>
            </div>
          </div>
        ) : (
          <div className="mt-4 text-sm text-slate-400" data-testid="ca-sectors-empty">Aucun chiffre d'affaires ce mois</div>
        )}
      </div>

      <div className="bg-white rounded-lg border border-slate-200">
        <div className="px-5 py-4 border-b border-slate-100 text-sm font-semibold text-slate-900">Derniers bons de commande</div>
        <div className="divide-y divide-slate-100" data-testid="recent-orders-list">
          {recent.length === 0 && <div className="px-5 py-6 text-sm text-slate-400">Aucun bon pour le moment.</div>}
          {recent.map((o) => {
            const M = MODULES[o.module];
            const st = ORDER_STATUS[o.status];
            return (
              <div key={o.id} data-testid={`recent-order-${o.id}`} className="px-5 py-3 flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3 min-w-0">
                  {M ? <M.icon size={16} style={{ color: M.color }} className="shrink-0" /> : <ReceiptText size={16} className="text-violet-500 shrink-0" />}
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-900 truncate">{o.order_number} — {orderSummary(o)}</div>
                    <div className="text-xs text-slate-500">{fmtDate(o.scheduled_date)}</div>
                  </div>
                </div>
                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${st.cls}`}>{st.label}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
